// =============================================================================
// Library Import Page — Excel/CSV bulk title import (Phase 1)
// =============================================================================
// Standalone import lane (NOT tied to upload batches): upload a spreadsheet,
// map the SKU + title columns (auto-detected), configure negative phrases /
// overwrite, run a mandatory dry-run, then apply. The same file is re-sent on
// every step (the server is stateless); the full report CSV is generated
// client-side from the dry-run/apply JSON.
// =============================================================================

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Card, CardHeader, Input, useConfirm } from '@tkpod/ui';
import {
  parseNegativePhrases,
  LIBRARY_IMPORT_MAX_FILE_BYTES,
  LIBRARY_IMPORT_PREVIEW_ROWS,
  LIBRARY_MAX_EXCEL_ROWS,
  type LibraryTitleImportApplyResult,
  type LibraryTitleImportDryRunResult,
  type LibraryTitleImportOptions,
  type LibraryTitleImportParseResult,
  type LibraryTitleImportRow,
  type LibraryTitleImportRowStatus,
} from '@tkpod/shared';
import { getCsrfToken } from '@/lib/csrf';
import { toast } from '@/lib/toast';

const ACCEPT = '.csv,.xlsx';
const MAX_FILE_MB = Math.round(LIBRARY_IMPORT_MAX_FILE_BYTES / 1024 / 1024);

// --- API (multipart — raw fetch, same pattern as catalog CSV import) ---------

async function postImport<T>(
  path: 'parse' | 'dry-run' | 'apply',
  file: File,
  options?: LibraryTitleImportOptions,
): Promise<T> {
  const formData = new FormData();
  // Options BEFORE the file so the server sees them as soon as it reads the
  // file part (fields sent after the file only appear post-toBuffer()).
  if (options) formData.append('options', JSON.stringify(options));
  formData.append('file', file);

  const baseUrl = import.meta.env.VITE_API_URL as string;
  const headers: Record<string, string> = {};
  const csrfToken = getCsrfToken();
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

  const res = await fetch(`${baseUrl}/api/v1/admin/library/import/${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });
  const json = (await res.json().catch(() => null)) as
    | { data?: T; message?: string | string[] }
    | null;
  if (!res.ok) {
    const message = Array.isArray(json?.message) ? json.message.join('; ') : json?.message;
    throw new Error(message ?? `Request failed (${res.status})`);
  }
  return json!.data as T;
}

// --- report CSV (client-side) -------------------------------------------------

function csvCell(value: string | null | undefined): string {
  let v = value ?? '';
  // Formula-injection guard: neutralize cells Excel would evaluate (=, +, -, @).
  if (/^[=+\-@\t\r]/.test(v)) v = `'${v}`;
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

function downloadReportCsv(rows: LibraryTitleImportRow[], fileName: string): void {
  const lines = [
    'sku,row,status,current_title,new_title,message',
    ...rows.map((r) =>
      [
        csvCell(r.sku),
        String(r.rowNumber),
        r.status,
        csvCell(r.currentTitle ?? ''),
        csvCell(r.newTitle ?? ''),
        csvCell(r.message ?? ''),
      ].join(','),
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

// --- status presentation --------------------------------------------------------

const STATUS_META: Record<LibraryTitleImportRowStatus, { label: string; cls: string }> = {
  update: { label: 'Update', cls: 'border-green-500/40 bg-green-500/10 text-green-400' },
  skipped_existing: { label: 'Skipped (has title)', cls: 'border-amber-500/40 bg-amber-500/10 text-amber-500' },
  unchanged: { label: 'Unchanged', cls: 'border-brand-800 bg-surface-tertiary/60 text-text-tertiary' },
  unmatched: { label: 'No match', cls: 'border-red-500/40 bg-red-500/10 text-red-400' },
  conflict: { label: 'Conflict', cls: 'border-orange-500/40 bg-orange-500/10 text-orange-400' },
  invalid: { label: 'Invalid', cls: 'border-red-500/40 bg-red-500/10 text-red-400' },
};

function StatusBadge({ status }: { status: LibraryTitleImportRowStatus }) {
  const meta = STATUS_META[status];
  return (
    <span className={`whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

/**
 * Report list filter. Concrete statuses map 1:1; `problems` groups
 * conflict + invalid (they share one summary chip); `all` shows everything.
 */
type ReportFilter = 'all' | LibraryTitleImportRowStatus | 'problems';

const FILTER_LABELS: Record<ReportFilter, string> = {
  all: 'all',
  update: 'updates',
  skipped_existing: 'skipped',
  unchanged: 'unchanged',
  unmatched: 'no-match',
  conflict: 'conflict',
  invalid: 'invalid',
  problems: 'conflict + invalid',
};

function matchesFilter(row: LibraryTitleImportRow, filter: ReportFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'problems') return row.status === 'conflict' || row.status === 'invalid';
  return row.status === filter;
}

function SummaryChip({
  label,
  value,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: number;
  tone?: 'good' | 'warn' | 'bad';
  /** Highlight as the currently selected report filter. */
  active?: boolean;
  /** Present = the chip is a clickable filter; absent = informational only. */
  onClick?: () => void;
}) {
  const toneCls =
    tone === 'good' ? 'text-green-400' : tone === 'warn' ? 'text-amber-500' : tone === 'bad' ? 'text-red-400' : 'text-text-primary';
  const frame = active
    ? 'border-brand-500 bg-brand-500/10 ring-1 ring-brand-500'
    : 'border-brand-900 bg-surface-tertiary/40';
  const body = (
    <>
      <p className={`text-lg font-semibold ${toneCls}`}>{value}</p>
      <p className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</p>
    </>
  );
  if (!onClick) {
    return <div className={`rounded-lg border px-3 py-2 text-center ${frame}`}>{body}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={active ? 'Click again to show all entries' : `Show only ${label.toLowerCase()} entries`}
      className={`rounded-lg border px-3 py-2 text-center transition-colors hover:border-brand-600 ${frame}`}
    >
      {body}
    </button>
  );
}

// --- page ------------------------------------------------------------------------

export function LibraryImportPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { confirm, dialog } = useConfirm();

  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<LibraryTitleImportParseResult | null>(null);
  const [parsing, setParsing] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Mapping + options (reset whenever a new file is parsed).
  const [skuColumn, setSkuColumn] = useState<number | ''>('');
  const [titleColumn, setTitleColumn] = useState<number | ''>('');
  const [negativeText, setNegativeText] = useState('');
  const [cleanTitle, setCleanTitle] = useState(true);
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  const [dryRun, setDryRun] = useState<LibraryTitleImportDryRunResult | null>(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState<LibraryTitleImportApplyResult | null>(null);
  const [reportFilter, setReportFilter] = useState<ReportFilter>('update');

  const busy = parsing || dryRunning || applying;
  const options: LibraryTitleImportOptions | null = useMemo(() => {
    if (skuColumn === '' || titleColumn === '') return null;
    return {
      skuColumn,
      titleColumn,
      negativePhrases: parseNegativePhrases(negativeText),
      cleanTitle,
      overwriteExisting,
    };
  }, [skuColumn, titleColumn, negativeText, cleanTitle, overwriteExisting]);

  /** Any option change invalidates the current dry-run (apply is gated on it). */
  const invalidateDryRun = () => setDryRun(null);

  const reset = () => {
    setFile(null);
    setParsed(null);
    setSkuColumn('');
    setTitleColumn('');
    setNegativeText('');
    setCleanTitle(true);
    setOverwriteExisting(false);
    setDryRun(null);
    setApplied(null);
    setReportFilter('update');
  };

  const selectFile = async (picked: File | null) => {
    if (!picked || busy) return;
    const name = picked.name.toLowerCase();
    if (!name.endsWith('.csv') && !name.endsWith('.xlsx')) {
      toast('Only .csv and .xlsx files are accepted', 'warning');
      return;
    }
    if (picked.size > LIBRARY_IMPORT_MAX_FILE_BYTES) {
      toast(`File exceeds the ${MAX_FILE_MB} MB limit`, 'warning');
      return;
    }
    reset();
    setFile(picked);
    setParsing(true);
    try {
      const result = await postImport<LibraryTitleImportParseResult>('parse', picked);
      setParsed(result);
      setSkuColumn(result.detected.skuColumn ?? '');
      setTitleColumn(result.detected.titleColumn ?? '');
      setNegativeText(result.suggestedNegativePhrases.map((s) => s.phrase).join(', '));
    } catch (e) {
      toast((e as Error).message, 'error');
      setFile(null);
    } finally {
      setParsing(false);
    }
  };

  const runDryRun = async () => {
    if (!file || !options) return;
    setDryRunning(true);
    setApplied(null);
    try {
      const result = await postImport<LibraryTitleImportDryRunResult>('dry-run', file, options);
      setDryRun(result);
      // Writable rows are what the admin came to review; when there are none,
      // fall back to the full list so the screen is never empty.
      setReportFilter(result.summary.update > 0 ? 'update' : 'all');
    } catch (e) {
      toast((e as Error).message, 'error');
      setDryRun(null);
    } finally {
      setDryRunning(false);
    }
  };

  const runApply = async () => {
    if (!file || !options || !dryRun) return;
    const ok = await confirm({
      title: 'Apply title import',
      description: `${dryRun.summary.update} asset title(s) will be written${
        overwriteExisting ? ' (existing titles WILL be overwritten)' : ''
      }. This cannot be undone in bulk.`,
      confirmLabel: `Apply ${dryRun.summary.update} update(s)`,
      tone: overwriteExisting ? 'danger' : undefined,
    });
    if (!ok) return;
    setApplying(true);
    try {
      const result = await postImport<LibraryTitleImportApplyResult>('apply', file, options);
      setApplied(result);
      setReportFilter(result.summary.update > 0 ? 'update' : 'all');
      toast(`Import applied — ${result.updated} title(s) updated`, 'success');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setApplying(false);
    }
  };

  const columnOptions = (parsed?.headers ?? []).map((h, i) => ({
    value: i,
    label: h ? `${h}` : `(column ${i + 1})`,
  }));

  const report = applied?.report ?? dryRun?.report ?? null;
  const summary = applied?.summary ?? dryRun?.summary ?? null;

  // Filter BEFORE the preview slice so e.g. 18 updates buried in 500 no-match
  // rows are all visible. The CSV download always contains the full report.
  const filteredReport = useMemo(
    () => (report ? report.filter((r) => matchesFilter(r, reportFilter)) : null),
    [report, reportFilter],
  );

  /** Toggle a chip filter: clicking the active chip returns to "all". */
  const toggleFilter = (filter: ReportFilter) =>
    setReportFilter((current) => (current === filter ? 'all' : filter));

  return (
    <div className="space-y-6 pb-24">
      {dialog}
      <Button variant="ghost" size="sm" onClick={() => navigate('/library')}>← Back to library</Button>

      <Card>
        <CardHeader
          title="Excel import — titles"
          description={`Update asset titles in bulk from a CSV/XLSX export (e.g. Shopify products). Rows are matched to library assets by SKU across the whole library. Max ${MAX_FILE_MB} MB, ${LIBRARY_MAX_EXCEL_ROWS.toLocaleString()} unique SKUs per import.`}
        />

        {/* Step 1 — file */}
        {!parsed && (
          <div
            onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); void selectFile(e.dataTransfer.files?.[0] ?? null); }}
            onClick={() => !busy && inputRef.current?.click()}
            className={[
              'mt-4 rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors',
              busy
                ? 'cursor-not-allowed border-brand-900 bg-surface-tertiary/40 opacity-60'
                : dragOver
                  ? 'cursor-pointer border-brand-500 bg-brand-500/5'
                  : 'cursor-pointer border-brand-800 hover:border-brand-600',
            ].join(' ')}
          >
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-500/10">
              <svg className="h-6 w-6 text-brand-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m6.75 12-3-3m0 0-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
            </div>
            <p className="mt-3 text-sm font-medium text-text-primary">
              {parsing ? 'Reading file…' : 'Drag & drop a CSV or XLSX file here'}
            </p>
            {!busy && (
              <p className="mt-1 text-sm text-text-secondary">
                or <span className="font-medium text-brand-400 underline">click to browse</span>
              </p>
            )}
            <p className="mt-2 text-xs uppercase tracking-wide text-text-tertiary">Max file size: {MAX_FILE_MB} MB</p>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              disabled={busy}
              onChange={(e) => { void selectFile(e.target.files?.[0] ?? null); e.target.value = ''; }}
            />
          </div>
        )}

        {/* Step 2 — mapping + options */}
        {parsed && file && (
          <div className="mt-4 space-y-4">
            <div className="flex flex-col gap-2 rounded-xl border border-brand-900 bg-surface-tertiary/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-text-primary">
                <span className="font-medium">{file.name}</span>
                <span className="text-text-tertiary"> · {parsed.rowCount.toLocaleString()} data rows</span>
              </p>
              <Button variant="outline" size="sm" disabled={busy} onClick={reset}>Choose another file</Button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-text-secondary">SKU column</span>
                <select
                  value={skuColumn}
                  disabled={busy || !!applied}
                  onChange={(e) => { setSkuColumn(e.target.value === '' ? '' : Number(e.target.value)); invalidateDryRun(); }}
                  className="w-full rounded-lg border border-border bg-surface-secondary px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none disabled:opacity-60"
                >
                  <option value="">Select column…</option>
                  {columnOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-text-secondary">Title column</span>
                <select
                  value={titleColumn}
                  disabled={busy || !!applied}
                  onChange={(e) => { setTitleColumn(e.target.value === '' ? '' : Number(e.target.value)); invalidateDryRun(); }}
                  className="w-full rounded-lg border border-border bg-surface-secondary px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none disabled:opacity-60"
                >
                  <option value="">Select column…</option>
                  {columnOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <div>
              <span className="mb-1 block text-xs font-medium text-text-secondary">
                Negative phrases (comma-separated — removed from every title)
              </span>
              <Input
                placeholder="e.g. DTF heat transfer, dtf transfer"
                value={negativeText}
                disabled={busy || !!applied}
                onChange={(e) => { setNegativeText(e.target.value); invalidateDryRun(); }}
              />
              {parsed.suggestedNegativePhrases.length > 0 && (
                <p className="mt-1 text-xs text-text-tertiary">
                  Suggested from the file:{' '}
                  {parsed.suggestedNegativePhrases.map((s) => `“${s.phrase}” (${s.count}×)`).join(', ')}
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm text-text-primary">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand-500"
                  checked={cleanTitle}
                  disabled={busy || !!applied}
                  onChange={(e) => { setCleanTitle(e.target.checked); invalidateDryRun(); }}
                />
                Remove negative phrases from titles
              </label>
              <label className="flex items-center gap-2 text-sm text-text-primary">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand-500"
                  checked={overwriteExisting}
                  disabled={busy || !!applied}
                  onChange={(e) => { setOverwriteExisting(e.target.checked); invalidateDryRun(); }}
                />
                Overwrite existing titles
                <span className="text-xs text-text-tertiary">(off = fill empty titles only)</span>
              </label>
            </div>

            {/* Sample preview of the selected columns */}
            {options && parsed.sampleRows.length > 0 && (
              <div className="overflow-hidden rounded-xl border border-brand-900">
                <div className="border-b border-brand-900 bg-surface-tertiary/40 px-4 py-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                    Sample ({Math.min(parsed.sampleRows.length, 5)} of {parsed.rowCount.toLocaleString()} rows)
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-brand-900 text-left text-xs text-text-tertiary">
                      <th className="px-4 py-2 font-medium">SKU ({parsed.headers[options.skuColumn]})</th>
                      <th className="px-4 py-2 font-medium">Title ({parsed.headers[options.titleColumn]})</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-900">
                    {parsed.sampleRows.slice(0, 5).map((row, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 font-mono text-xs text-text-secondary">{row[options.skuColumn] || '—'}</td>
                        <td className="px-4 py-2 text-text-primary">{row[options.titleColumn] || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!applied && (
              <div className="flex justify-end">
                <Button loading={dryRunning} disabled={!options || busy} onClick={() => void runDryRun()}>
                  Run dry run →
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Step 3/4 — dry-run preview or apply result */}
      {summary && report && (
        <Card>
          <CardHeader
            title={applied ? 'Import applied' : 'Dry run — nothing written yet'}
            description={
              applied
                ? `${applied.updated} title(s) updated. Download the report for the full row-by-row outcome.`
                : 'Review the changes below, then apply. Only rows marked "Update" will be written.'
            }
            action={
              <Button variant="outline" size="sm" onClick={() => downloadReportCsv(report, `title-import-report-${Date.now()}.csv`)}>
                Download report (CSV)
              </Button>
            }
          />

          {applied && (
            <div className="mt-4 flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3">
              <svg className="h-6 w-6 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              <p className="text-sm font-medium text-text-primary">
                {applied.updated} asset title{applied.updated === 1 ? '' : 's'} updated.
              </p>
            </div>
          )}

          {/* Clickable chips filter the list below; "Rows"/"Unique SKUs" are informational. */}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
            <SummaryChip label="Rows" value={summary.rawRows} />
            <SummaryChip label="Unique SKUs" value={summary.uniqueSkus} />
            <SummaryChip
              label={applied ? 'Updated' : 'Updates'}
              value={summary.update}
              tone="good"
              active={reportFilter === 'update'}
              onClick={() => toggleFilter('update')}
            />
            <SummaryChip
              label="Skipped"
              value={summary.skippedExisting}
              tone="warn"
              active={reportFilter === 'skipped_existing'}
              onClick={() => toggleFilter('skipped_existing')}
            />
            <SummaryChip
              label="Unchanged"
              value={summary.unchanged}
              active={reportFilter === 'unchanged'}
              onClick={() => toggleFilter('unchanged')}
            />
            <SummaryChip
              label="No match"
              value={summary.unmatched}
              tone={summary.unmatched > 0 ? 'bad' : undefined}
              active={reportFilter === 'unmatched'}
              onClick={() => toggleFilter('unmatched')}
            />
            <SummaryChip
              label="Conflicts + invalid"
              value={summary.conflict + summary.invalid}
              tone={summary.conflict + summary.invalid > 0 ? 'bad' : undefined}
              active={reportFilter === 'problems'}
              onClick={() => toggleFilter('problems')}
            />
          </div>

          <div className="mt-4 overflow-hidden rounded-xl border border-brand-900">
            <div className="flex items-center justify-between gap-2 border-b border-brand-900 bg-surface-tertiary/40 px-4 py-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                {filteredReport!.length > LIBRARY_IMPORT_PREVIEW_ROWS
                  ? `First ${LIBRARY_IMPORT_PREVIEW_ROWS} of ${filteredReport!.length} ${FILTER_LABELS[reportFilter]} entries (full list in the CSV report)`
                  : `${filteredReport!.length} ${FILTER_LABELS[reportFilter]} entr${filteredReport!.length === 1 ? 'y' : 'ies'}`}
              </span>
              {reportFilter !== 'all' && (
                <button
                  type="button"
                  onClick={() => setReportFilter('all')}
                  className="text-xs font-medium text-brand-400 hover:underline"
                >
                  Show all {report.length}
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-surface-secondary">
                  <tr className="border-b border-brand-900 text-left text-xs text-text-tertiary">
                    <th className="px-4 py-2 font-medium">SKU</th>
                    <th className="px-4 py-2 font-medium">Row</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Current title</th>
                    <th className="px-4 py-2 font-medium">New title</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-900">
                  {filteredReport!.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-text-tertiary">
                        No {FILTER_LABELS[reportFilter]} entries — pick another card above.
                      </td>
                    </tr>
                  )}
                  {filteredReport!.slice(0, LIBRARY_IMPORT_PREVIEW_ROWS).map((r, i) => (
                    <tr key={`${r.sku}-${i}`}>
                      <td className="px-4 py-2 font-mono text-xs text-text-secondary">{r.sku}</td>
                      <td className="px-4 py-2 text-xs text-text-tertiary">{r.rowNumber}</td>
                      <td className="px-4 py-2">
                        <StatusBadge status={r.status} />
                        {r.message && <p className="mt-0.5 text-[11px] text-text-tertiary">{r.message}</p>}
                      </td>
                      <td className="px-4 py-2 text-xs text-text-secondary">{r.currentTitle ?? '—'}</td>
                      <td className="px-4 py-2 text-xs text-text-primary">{r.newTitle ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            {applied ? (
              <Button onClick={reset}>Start new import</Button>
            ) : (
              <Button
                loading={applying}
                disabled={busy || dryRun === null || dryRun.summary.update === 0}
                title={dryRun?.summary.update === 0 ? 'Nothing to update' : undefined}
                onClick={() => void runApply()}
              >
                Apply {dryRun?.summary.update ?? 0} update{(dryRun?.summary.update ?? 0) === 1 ? '' : 's'} →
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
